import type { RecurringPaymentCadence } from '@/lib/api';

/**
 * Which occurrence a charge settles — the client-side mirror of the backend's
 * `RecurringOccurrenceCalendar`. Extracted from the home widget's cycle model so the
 * Recorrentes page resolves "paid this period?" through the same rule instead of a
 * second, drifting copy of it.
 */

const MAX_ADVANCE_DAYS = 12;
const MAX_LATE_DAYS = 7;
const DAY_MS = 86_400_000;

export function monthKeyOf(iso: string | null | undefined): string | null {
  // Compare on the ISO 'YYYY-MM' prefix so a midnight-UTC date can't slip a
  // month under the viewer's timezone.
  return iso && iso.length >= 7 ? iso.slice(0, 7) : null;
}

/**
 * The due date in a given month, clamped to the month's length. Date.UTC normalises an
 * out-of-range month index (e.g. -1 or 12) into the neighbouring year, so callers can pass
 * `monthIndex ± 1` freely. UTC throughout keeps the comparison timezone-safe.
 */
export function dueUtcMs(year: number, monthIndex0: number, dueDay: number): number {
  const anchor = new Date(Date.UTC(year, monthIndex0, 1));
  const y = anchor.getUTCFullYear();
  const m = anchor.getUTCMonth();
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Date.UTC(y, m, Math.min(dueDay, lastDay));
}

export function monthKeyFromParts(year: number, monthIndex0: number): string {
  const d = new Date(Date.UTC(year, monthIndex0, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Attributes a charge to the occurrence it settles rather than the calendar month it landed
 * in. A charge may fall up to 12 days before the next month's due date (advance payment) or
 * 7 days after the previous month's (late), else it stays in its own calendar month. Returns
 * the occurrence's 'YYYY-MM' key — so a bill due early in the month but paid on the prior
 * month's last Monday still reads as paid this cycle (VER-254). Falls back to the plain month
 * key when there is no monthly due day to shift around.
 */
export function occurrenceMonthKeyOf(
  iso: string | null | undefined,
  dayOfMonth: number | null,
  cadence: RecurringPaymentCadence,
): string | null {
  const datePart = iso && iso.length >= 10 ? iso.slice(0, 10) : null;
  if (!datePart) return monthKeyOf(iso);
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return monthKeyOf(iso);
  if (cadence !== 'Monthly' || !dayOfMonth || dayOfMonth <= 0) return datePart.slice(0, 7);

  const monthIndex0 = month - 1;
  const chargeMs = Date.UTC(year, monthIndex0, day);
  const distanceThis = Math.abs(chargeMs - dueUtcMs(year, monthIndex0, dayOfMonth)) / DAY_MS;
  const daysEarlyForNext = (dueUtcMs(year, monthIndex0 + 1, dayOfMonth) - chargeMs) / DAY_MS;
  const daysLateForPrev = (chargeMs - dueUtcMs(year, monthIndex0 - 1, dayOfMonth)) / DAY_MS;

  if (daysEarlyForNext < distanceThis && daysEarlyForNext <= MAX_ADVANCE_DAYS) {
    return monthKeyFromParts(year, monthIndex0 + 1);
  }
  if (daysLateForPrev < distanceThis && daysLateForPrev <= MAX_LATE_DAYS) {
    return monthKeyFromParts(year, monthIndex0 - 1);
  }
  return datePart.slice(0, 7);
}
